import { cookies, createVar } from "@rangojs/router";
import type { MiddlewareContext } from "@rangojs/router";

// --- Types ---

export interface SessionData {
  email: string;
}

// --- Context variable ---

export const SessionUser = createVar<SessionData>();

// --- AES-GCM encryption helpers ---

function hexToBuffer(hex: string): ArrayBuffer {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", hexToBuffer(secret), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

export async function encrypt(data: string, secret: string): Promise<string> {
  const key = await importKey(secret);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(data);
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  return `${bufferToBase64url(iv.buffer)}.${bufferToBase64url(ciphertext)}`;
}

export async function decrypt(token: string, secret: string): Promise<string> {
  const [ivPart, ciphertextPart] = token.split(".");
  if (!ivPart || !ciphertextPart) throw new Error("Invalid token format");

  const key = await importKey(secret);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(base64urlToBuffer(ivPart)) },
    key,
    base64urlToBuffer(ciphertextPart),
  );
  return new TextDecoder().decode(decrypted);
}

// --- Helper to extract SESSION_SECRET from env ---

export function getSessionSecret(env: { SESSION_SECRET: string }): string {
  return env.SESSION_SECRET;
}

// --- Session middleware ---

export const sessionMiddleware = async (ctx: MiddlewareContext, next: () => Promise<Response>) => {
  const cookie = cookies().get("session")?.value;
  console.log("cookie", cookie);

  if (cookie) {
    try {
      const json = await decrypt(cookie, getSessionSecret(ctx.env));
      const data: SessionData = JSON.parse(json);
      ctx.set(SessionUser, data);
    } catch {
      // Invalid/tampered cookie — ignore silently
    }
  }

  await next();
};
